<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useTranslationStore } from "@/stores/translation";
import { useWeeklyMenuStore } from "@/stores/weeklyMenu";
import wechatQr from "@/assets/CHDS-Wechat.webp";

const translationStore = useTranslationStore();
const weekly = useWeeklyMenuStore();
const route = useRoute();
const t = (label, modules) => translationStore.translate(label, modules);
// Link straight to the live ordering pages (never through a redirect) and keep /zh visitors on /zh
const zh = computed(() => String(route.path || "").startsWith("/zh"));
const p = (to) => (zh.value ? (to === "/" ? "/zh" : `/zh${to}`) : to);
const orderPath = computed(() => p(weekly.enabled ? "/menu" : "/ordernow"));
const packPath = computed(() => p(weekly.enabled ? "/packages" : "/box"));
const guidesLabel = computed(() => (translationStore.currentLanguage === "zh" ? "指南" : "Guides"));
const year = new Date().getFullYear();

const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
</script>

<template>
	<footer class="chds-footer">
		<div class="cf-inner">
			<div class="cf-top">
				<!-- Brand -->
				<div class="cf-brand">
					<img class="cf-logo" src="/chds-logo.webp" alt="CHDS 吃荤搭素" />
					<p class="cf-tagline">{{ t("footer_tagline", ["footer"]) || "Chinese-infused meals, made healthy — chef-cooked and delivered across Melbourne." }}</p>
					<div class="cf-social">
						<a href="https://www.facebook.com/" target="_blank" rel="noopener" aria-label="Facebook"><i class="fab fa-facebook-f"></i></a>
						<a href="https://www.instagram.com/" target="_blank" rel="noopener" aria-label="Instagram"><i class="fab fa-instagram"></i></a>
						<a href="mailto:info@chds.com.au" aria-label="Email"><i class="fa-solid fa-envelope"></i></a>
					</div>
				</div>

				<!-- Explore -->
				<nav class="cf-col">
					<h4>{{ t("explore", ["footer"]) || "Explore" }}</h4>
					<router-link :to="p('/')" @click="scrollToTop">{{ t("home", ["footer"]) }}</router-link>
					<router-link :to="orderPath" @click="scrollToTop">{{ t("order_now", ["footer"]) }}</router-link>
					<router-link :to="packPath" @click="scrollToTop">{{ t("nav_label", ["box"]) }}</router-link>
					<router-link :to="p('/find-my-meals')" @click="scrollToTop">{{ translationStore.currentLanguage === "zh" ? "为我配餐" : "Find my meals" }}</router-link>
					<router-link :to="p('/guides')" @click="scrollToTop">{{ guidesLabel }}</router-link>
					<router-link :to="p('/ourstory')" @click="scrollToTop">{{ t("our_story", ["footer"]) }}</router-link>
					<router-link :to="p('/contact-us')" @click="scrollToTop">{{ t("contact_us", ["footer"]) }}</router-link>
				</nav>

				<!-- Legal -->
				<nav class="cf-col">
					<h4>{{ t("legal", ["footer"]) || "Legal" }}</h4>
					<router-link to="/terms" @click="scrollToTop">{{ t("terms_conditions", ["footer"]) || "Terms & Conditions" }}</router-link>
					<router-link to="/privacy" @click="scrollToTop">{{ t("privacy_policy", ["footer"]) || "Privacy Policy" }}</router-link>
					<router-link to="/refund-policy" @click="scrollToTop">{{ t("refunds_returns", ["footer"]) || "Refunds & Returns" }}</router-link>
					<router-link to="/contact-us" @click="scrollToTop">{{ t("contact_us", ["footer"]) }}</router-link>
				</nav>

				<!-- Contact + hours + QR -->
				<div class="cf-col cf-contact">
					<h4>{{ t("get_in_touch", ["footer"]) || "Get in touch" }}</h4>
					<a class="cf-line" href="https://maps.google.com/?q=243 Burwood Road Hawthorn East VIC 3122" target="_blank" rel="noopener">
						<i class="fa-solid fa-location-dot"></i><span>{{ t("restaurant_address", ["footer"]) }}</span>
					</a>
					<a class="cf-line" href="mailto:info@chds.com.au"><i class="fa-solid fa-envelope"></i><span>{{ t("email_address", ["footer"]) }}</span></a>
					<a class="cf-line" href="tel:+61489198036"><i class="fa-solid fa-phone"></i><span>{{ t("customer_care_number", ["footer"]) }}</span></a>
					<div class="cf-line cf-hours"><i class="fa-solid fa-clock"></i><span>{{ t("monday_wednesday_friday", ["footer"]) }} · 2:30–8:00pm</span></div>
					<div class="cf-qr">
						<img :src="wechatQr" alt="CHDS WeChat" />
						<span>{{ t("scan_wechat", ["footer"]) || "Scan to add us on WeChat" }}</span>
					</div>
				</div>
			</div>

			<div class="cf-bottom">
				<p class="cf-copy">© {{ year }} CHDS <span class="cf-cn">吃荤搭素</span> · {{ t("all_rights_reserved", ["footer"]) || "All rights reserved." }}</p>
				<div class="cf-bottom-right">
					<router-link to="/terms" @click="scrollToTop">{{ t("terms", ["footer"]) || "Terms" }}</router-link>
					<span class="cf-dot">·</span>
					<router-link to="/privacy" @click="scrollToTop">{{ t("privacy", ["footer"]) || "Privacy" }}</router-link>
					<span class="cf-made">🥢 {{ t("made_in_melbourne", ["footer"]) || "Made fresh in Melbourne" }}</span>
				</div>
			</div>
		</div>
	</footer>
</template>

<style scoped>
.chds-footer {
	background: radial-gradient(1200px 400px at 15% -20%, rgba(134, 173, 63, 0.16), transparent 60%), linear-gradient(180deg, #2b3a22 0%, #212d1a 100%);
	color: #d9e0cf;
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.cf-inner { max-width: 1240px; margin: 0 auto; padding: clamp(40px, 6vw, 64px) clamp(20px, 5vw, 48px) 0; }
.cf-top { display: grid; grid-template-columns: 1.6fr 1fr 1fr 1.5fr; gap: clamp(28px, 4vw, 56px); padding-bottom: 40px; }
/* Brand */
.cf-logo { width: 168px; max-width: 60%; height: auto; margin-bottom: 16px; filter: brightness(0) invert(1); opacity: 0.96; }
.cf-tagline { font-size: 14px; line-height: 1.6; color: #b7c1a6; max-width: 320px; margin: 0 0 20px; }
.cf-social { display: flex; gap: 10px; }
.cf-social a {
	width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
	background: rgba(255, 255, 255, 0.08); color: #e7ecdd; text-decoration: none; font-size: 15px;
	transition: background 0.18s ease, transform 0.18s ease, color 0.18s ease;
}
.cf-social a:hover { background: #B6D452; color: #23400f; transform: translateY(-3px); }
/* Columns */
.cf-col h4 { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #fff; margin: 4px 0 18px; }
.cf-col a, .cf-col .cf-line { display: block; color: #c4cdb5; text-decoration: none; font-size: 14.5px; margin-bottom: 12px; transition: color 0.15s ease, padding-left 0.15s ease; }
.cf-col > a:hover { color: #cfe38a; padding-left: 4px; }
/* Contact */
.cf-contact .cf-line { display: flex; align-items: flex-start; gap: 10px; line-height: 1.45; }
.cf-contact .cf-line i { color: #86ad3f; margin-top: 3px; width: 16px; flex: none; }
.cf-contact a.cf-line:hover span { color: #cfe38a; }
.cf-hours { color: #b7c1a6; }
.cf-qr { display: flex; align-items: center; gap: 12px; margin-top: 16px; }
.cf-qr img { width: 78px; height: 78px; border-radius: 10px; background: #fff; padding: 5px; }
.cf-qr span { font-size: 12px; color: #9aa588; max-width: 130px; line-height: 1.4; }
/* Bottom */
.cf-bottom {
	display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
	border-top: 1px solid rgba(255, 255, 255, 0.1); padding: 20px 0 22px; margin-top: 8px;
}
.cf-copy { font-size: 13px; color: #98a487; margin: 0; }
.cf-cn { letter-spacing: 2px; }
.cf-bottom-right { display: flex; align-items: center; gap: 12px; font-size: 13px; }
.cf-bottom-right a { color: #b7c1a6; text-decoration: none; }
.cf-bottom-right a:hover { color: #cfe38a; }
.cf-dot { color: #5c6a4b; }
.cf-made { color: #98a487; margin-left: 6px; }

@media (max-width: 900px) {
	.cf-top { grid-template-columns: 1fr 1fr; gap: 32px 24px; }
	.cf-brand { grid-column: 1 / -1; }
	.cf-contact { grid-column: 1 / -1; }
}
@media (max-width: 560px) {
	.cf-top { grid-template-columns: 1fr; text-align: left; padding-bottom: 30px; }
	.cf-logo { width: 150px; }
	.cf-tagline { max-width: 100%; }
	.cf-brand { text-align: center; }
	.cf-brand .cf-tagline { margin-left: auto; margin-right: auto; }
	.cf-social { justify-content: center; }
	.cf-bottom { justify-content: center; text-align: center; flex-direction: column; gap: 10px; padding-bottom: 26px; }
	.cf-bottom-right { flex-wrap: wrap; justify-content: center; }
	.cf-made { width: 100%; margin: 4px 0 0; }
}
</style>
